import React, { useId, useState, useMemo } from 'react';
import { getCategoryColor, normalizeCategoryList } from '../../services/eventCategories';
import './CategoryToggleGroup.css';

const CategoryToggleGroup = ({
  options,
  selected = [],
  onChange,
  label = 'Categories: ',
  legend = null,
  showAll = true,
  allLabel = 'All',
  allCount = null,
  showCounts = false,
  disabled = false,
  className = '',
  multiple = true,
  searchable = false,
  searchPlaceholder = 'Search categories…',
  allowCustomAdd = false,
  maxVisible = null,
  moreLabel = '',
  searchFirst = false,
}) => {
  const [query, setQuery] = useState('');
  const [addingCustom, setAddingCustom] = useState(false);
  const [customDraft, setCustomDraft] = useState('');
  const [expanded, setExpanded] = useState(false);
  const searchId = useId();
  const customId = useId();
  const selectedCategories = useMemo(() => normalizeCategoryList(selected), [selected]);

  const {
    optionByLabel,
    selectedSet,
    selectedOptions,
    term,
    filteredUnselected,
    chipsToShow,
    needsCollapse,
    hiddenCount,
    draftLabel,
    draftExists,
    canAddFromSearch,
  } = useMemo(() => {
    const map = new Map();
    options.forEach((option) => {
      const entry = typeof option === 'string'
        ? { label: option, color: getCategoryColor(option), count: undefined }
        : {
            ...option,
            color: option.color || getCategoryColor(option.label),
          };
      if (entry.label) map.set(entry.label, entry);
    });

    selectedCategories.forEach((category) => {
      if (!map.has(category)) {
        map.set(category, {
          label: category,
          color: getCategoryColor(category),
          count: undefined,
        });
      }
    });

    const normalizedOptions = Array.from(map.values());
    const selSet = new Set(selectedCategories);
    const selectedOptions = selectedCategories
      .map((category) => map.get(category))
      .filter(Boolean);

    const term = searchable ? query.trim().toLowerCase() : '';
    const unselectedOptions = normalizedOptions.filter((option) => !selSet.has(option.label));
    const filteredUnselected = !term
      ? unselectedOptions
      : unselectedOptions.filter(({ label: category }) => (
        String(category || '').toLowerCase().includes(term)
      ));

    const visibleOptions = searchable
      ? [...selectedOptions, ...filteredUnselected]
      : [...selectedOptions, ...unselectedOptions];

    const chipOptions = searchable ? filteredUnselected : visibleOptions;
    const limit =
      typeof maxVisible === 'number' && maxVisible > 0 ? Math.floor(maxVisible) : null;
    const collapse = limit != null && chipOptions.length > limit;
    const hiddenCount = collapse ? Math.max(0, chipOptions.length - limit) : 0;
    let chips = chipOptions;
    if (collapse && !expanded) {
      const head = chipOptions.slice(0, limit);
      const headLabels = new Set(head.map((option) => option.label));
      const selectedExtras = chipOptions.filter(
        (option) => selSet.has(option.label) && !headLabels.has(option.label)
      );
      chips = [...head, ...selectedExtras];
    }

    const draft = searchable ? query.trim() : '';
    const exists = Boolean(
      draft
      && [...map.keys()].some(
        (existing) => String(existing).toLowerCase() === draft.toLowerCase()
      )
    );
    const canAdd = Boolean(
      allowCustomAdd && searchable && draft && !exists && !selSet.has(draft)
    );

    return {
      optionByLabel: map,
      selectedSet: selSet,
      selectedOptions,
      term,
      filteredUnselected,
      chipsToShow: chips,
      needsCollapse: collapse,
      hiddenCount,
      draftLabel: draft,
      draftExists: exists,
      canAddFromSearch: canAdd,
    };
  }, [options, selectedCategories, searchable, query, maxVisible, expanded, allowCustomAdd]);

  const toggle = (category) => {
    if (!multiple) {
      onChange(selectedCategories.includes(category) ? [] : [category]);
      return;
    }
    const next = selectedCategories.includes(category)
      ? selectedCategories.filter((item) => item !== category)
      : [...selectedCategories, category];
    onChange(next);
  };

  const addCategoryLabel = (labelText) => {
    const nextLabel = String(labelText || '').trim();
    if (!nextLabel) return;
    if (!selectedCategories.includes(nextLabel)) {
      onChange(multiple ? [...selectedCategories, nextLabel] : [nextLabel]);
    }
    setQuery('');
    setCustomDraft('');
    setAddingCustom(false);
  };

  const submitCustom = () => {
    addCategoryLabel(customDraft);
  };

  const formatLabel = (text, count) => {
    if (!showCounts || typeof count !== 'number') return text;
    return (
      <>
        <span className="category-toggle__name">{text}</span>
        <span className="category-toggle__count">{count}</span>
      </>
    );
  };

  const renderChip = ({ label: category, displayLabel, color, count }) => {
    const isSelected = selectedCategories.includes(category);
    const chipText = displayLabel || category;
    return (
      <button
        key={category}
        type="button"
        className={`category-toggle${isSelected ? ' is-selected' : ''}`}
        style={{ '--category-highlight': color }}
        aria-pressed={isSelected}
        onClick={() => toggle(category)}
        disabled={disabled}
      >
        {formatLabel(chipText, count)}
      </button>
    );
  };

  return (
    <div
      className={`category-toggle-group${searchable ? ' category-toggle-group--searchable' : ''} ${className}`.trim()}
      role="group"
      aria-label={label}
    >
      {searchable && !searchFirst && selectedOptions.length > 0 && (
        <div className="category-toggle-group__selected" aria-label="Selected categories">
          {selectedOptions.map(renderChip)}
        </div>
      )}
      {searchable && (
        <div className="category-toggle-group__search">
          <label className="category-toggle-group__search-label" htmlFor={searchId}>
            {searchPlaceholder}
          </label>
          <input
            id={searchId}
            type="search"
            className="category-toggle-group__search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canAddFromSearch) {
                e.preventDefault();
                addCategoryLabel(draftLabel);
              }
            }}
            placeholder={searchPlaceholder}
            disabled={disabled}
            autoComplete="off"
          />
        </div>
      )}
      {searchable && searchFirst && selectedOptions.length > 0 && (
        <div className="category-toggle-group__selected" aria-label="Selected categories">
          {selectedOptions.map(renderChip)}
        </div>
      )}
      <div className="category-toggle-group__chips">
        {legend ? (
          <span className="category-toggle-group__legend">{legend}</span>
        ) : null}
        {showAll && (
          <button
            type="button"
            className={`category-toggle${selectedCategories.length === 0 ? ' is-selected is-all' : ''}`}
            aria-pressed={selectedCategories.length === 0}
            onClick={() => onChange([])}
            disabled={disabled}
          >
            {formatLabel(allLabel, allCount)}
          </button>
        )}
        {chipsToShow.map(renderChip)}
        {needsCollapse && !expanded && (
          <button
            type="button"
            className="category-toggle category-toggle--more"
            onClick={() => setExpanded(true)}
            disabled={disabled}
          >
            {moreLabel || `SHOW MORE +${hiddenCount}`}
          </button>
        )}
        {needsCollapse && expanded && (
          <button
            type="button"
            className="category-toggle category-toggle--more"
            onClick={() => setExpanded(false)}
            disabled={disabled}
          >
            Show less
          </button>
        )}
        {canAddFromSearch && (
          <button
            type="button"
            className="category-toggle category-toggle--add-confirm"
            onClick={() => addCategoryLabel(draftLabel)}
            disabled={disabled}
          >
            + Add “{draftLabel}”
          </button>
        )}
        {searchable && term && !canAddFromSearch && filteredUnselected.length === 0 && selectedOptions.length === 0 && (
          <span className="category-toggle-group__empty">No matches</span>
        )}
        {searchable && term && !canAddFromSearch && filteredUnselected.length === 0 && selectedOptions.length > 0 && (
          <span className="category-toggle-group__empty">No other matches</span>
        )}
        {allowCustomAdd && !addingCustom && !searchable && (
          <button
            type="button"
            className="category-toggle category-toggle--add"
            onClick={() => setAddingCustom(true)}
            disabled={disabled}
            aria-label="Add custom category"
            title="Add custom category"
          >
            +
          </button>
        )}
      </div>
      {allowCustomAdd && addingCustom && (
        <div className="category-toggle-group__custom">
          <label className="category-toggle-group__search-label" htmlFor={customId}>
            New category
          </label>
          <input
            id={customId}
            type="text"
            className="category-toggle-group__search-input"
            value={customDraft}
            onChange={(e) => setCustomDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitCustom();
              }
              if (e.key === 'Escape') {
                setCustomDraft('');
                setAddingCustom(false);
              }
            }}
            placeholder="Type a category…"
            disabled={disabled}
            autoFocus
            autoComplete="off"
          />
          <button
            type="button"
            className="category-toggle category-toggle--add-confirm"
            onClick={submitCustom}
            disabled={disabled || !customDraft.trim()}
          >
            Add
          </button>
          <button
            type="button"
            className="category-toggle"
            onClick={() => {
              setCustomDraft('');
              setAddingCustom(false);
            }}
            disabled={disabled}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default React.memo(CategoryToggleGroup);
